import React, { useState } from 'react'
import { TextField } from '@mui/material'
import { color } from '@trivie/ui-web/theme'
import { Button } from '@trivie/ui-web/components/button'
import { Text } from '@trivie/ui-web/components/text'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router-dom'
import SurveyCreationHeader from './survey-creation-header'
import { AddRounded } from '@mui/icons-material'
import { RadioQuestion } from './radio-question'
import { ShortAnswerQuestion } from './short-answer-question'
import { RangeQuestion } from './range-question'
import { TrueFalseQuestion } from './true-false-question'
import { useStores } from '@trivie/core'
import { AssignSurveyDialog } from '../assign-survey/assign-survey'
import { SurveyHelper } from '@trivie/core/src/helpers'
import { getNodeId, Instance } from 'mobx-state-tree'
import { SurveyQuestion } from '@trivie/core/src/models/Survey'
import { ConfirmDialog } from '@trivie/ui-web/components/confirm-dialog'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'
import { RadioCard } from '../../subscriptions/create-subscription/radio-card'
import { DateTime } from 'luxon'

export const CreateSurveyScreen: React.FunctionComponent = observer(() => {
  const history = useHistory()
  const { surveyStore } = useStores()
  const { postSurveyAssignDetails } = surveyStore

  const [survey] = useState(SurveyHelper.createNewSurvey())

  const [showPathPromptDialog, setShowPathPromptDialog] = useState(false)
  const [mode, setMode] = useState('standard')
  const [showAssignDialog, setShowAssignDialog] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showConflictDialog, setShowConflictDialog] = useState('')

  const handlePublish = async () => {
    survey.setShowErrors(true)
    survey.setShowTitleError(true)
    if (survey.valid) {
      setShowPathPromptDialog(true)
    }
  }

  const handleCreate = () => {
    if (mode === 'path') {
      surveyStore.createSurvey(survey).then((r: any) => {
        if (r?.kind === 'conflict' && r?.message) {
          setShowConflictDialog(r.message)
        } else {
          postSurveyAssignDetails(r, {
            ...survey,
            id: r,
            start_datetime: DateTime.now().toUTC().toISO(),
            end_datetime: null,
            is_anonymous: false,
            groups: [] as any,
            users: [] as any,
          })
            .then((resp: any) => {
              if (resp.kind !== 'conflict') {
                console.log(resp)
              }
            })
            .finally(() => {
              history.push(`/activities/surveys/live`)
            })
        }
      })
    } else {
      surveyStore.createSurvey(survey).then((r: any) => {
        if (r?.kind === 'conflict' && r?.message) {
          setShowConflictDialog(r.message)
        } else {
          setShowAssignDialog(true)
        }
      })
    }
  }

  const [draggingQuestionID, setDraggingQuestionID] = useState<null | number>(null)

  const handleDrag = (question: Instance<typeof SurveyQuestion>) =>
    setDraggingQuestionID(getNodeId(question))

  const handleDrop = (question: Instance<typeof SurveyQuestion>) => {
    const { questions } = survey
    const dragQuestion = questions?.find((q) => getNodeId(q) === draggingQuestionID)
    const dropQuestion = questions?.find((q) => getNodeId(q) === getNodeId(question))

    if (dragQuestion && dropQuestion) {
      survey.reorder(questions?.indexOf(dragQuestion) ?? 0, questions?.indexOf(dropQuestion) ?? 0)
    }
  }

  return (
    <>
      {showPathPromptDialog && (
        <Dialog
          maxWidth="md"
          title="Create Survey"
          open={showPathPromptDialog}
          onClose={() => setShowPathPromptDialog(false)}
          actions={
            <div style={{ display: 'flex', flex: 1, justifyContent: 'flex-end', gap: '12px' }}>
              {mode === 'path' ? (
                <>
                  <Button
                    sx={{ display: 'none' }}
                    variant="outlined"
                    onClick={() => {}}
                    text={''}
                  />
                  <Button
                    onClick={() => {
                      handleCreate()
                    }}
                    text={'Create'}
                  />
                </>
              ) : (
                <>
                  <Button
                    onClick={() => {
                      setShowPathPromptDialog(false)
                      handleCreate()
                      history.push(`/activities/surveys/unscheduled`)
                    }}
                    variant="outlined"
                    text={'Assign Later'}
                  />
                  <Button
                    onClick={() => {
                      setShowPathPromptDialog(false)
                      handleCreate()
                    }}
                    text={'Assign Now'}
                  />
                </>
              )}
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
            <RadioCard
              selected={mode === 'standard'}
              onClick={() => setMode('standard')}
              label="Standard Survey"
              description="Create a standard survey and choose whether to assign this survey now or later"
            />
            <RadioCard
              selected={mode === 'path'}
              onClick={() => setMode('path')}
              label="Learning Path Survey"
              description="Create a survey to be used within a Learning Path"
            />
          </div>
        </Dialog>
      )}
      {Boolean(showConflictDialog) && (
        <ConfirmDialog
          danger
          open={Boolean(showConflictDialog)}
          title="Duplicate Survey"
          message={showConflictDialog}
          confirmButton="Okay"
          hideCancel
          onConfirm={() => setShowConflictDialog('')}
          onClose={() => setShowConflictDialog('')}
        />
      )}
      {showCancelDialog && (
        <ConfirmDialog
          open={showCancelDialog}
          title="Back"
          message="Are you sure you want to go back? Your content will not be published."
          confirmButton="Confirm"
          onConfirm={history.goBack}
          onClose={() => setShowCancelDialog(false)}
          handleCancel={() => setShowCancelDialog(false)}
        />
      )}
      {showAssignDialog && (
        <AssignSurveyDialog
          open={showAssignDialog}
          requestClose={() => history.push(`/activities/surveys/unscheduled`)}
        />
      )}
      <div style={ROOT}>
        <SurveyCreationHeader
          showErrorCount={survey.showErrors}
          onBack={() => setShowCancelDialog(true)}
          errorCount={survey.errorCount}
          publishMessage="Your survey has not been published. Are you sure you want to exit without saving?"
          onPublish={handlePublish}
          titleHeader={<Text text="Create Survey" variant="labelEmphasized" />}
          publishBtnText="Save"
          disablePublish={survey.questions?.length === 0}
        />
        <div style={CONTAINER}>
          <div style={FORM}>
            <TextField
              variant="standard"
              multiline
              onBlur={() => survey.setShowTitleError(true)}
              inputProps={{
                'aria-label': 'Survey Title',
                style: {
                  ...SURVEY_TITLE_INPUT,
                  color:
                    survey.showTitleError &&
                    (!survey.hasTitle || survey.longTitle || showConflictDialog)
                      ? color.red
                      : color.black,
                },
              }}
              error={Boolean(
                survey.showTitleError &&
                  (!survey.hasTitle || survey.longTitle || showConflictDialog),
              )}
              helperText={
                survey.showTitleError && !survey.hasTitle ? 'Survey name cannot be blank' : ''
              }
              size="medium"
              placeholder="Enter a survey name"
              value={survey.title}
              onChange={(e: any) => survey.setTitle(e.target.value)}
            />
            {survey.questions &&
              survey?.questions.map((question) => {
                switch (question.question_type) {
                  case 'open_text':
                    return (
                      <ShortAnswerQuestion
                        handleDrop={() => handleDrop(question)}
                        handleDrag={() => handleDrag(question)}
                        question={question}
                      />
                    )
                  case 'range_response':
                    return (
                      <RangeQuestion
                        handleDrop={() => handleDrop(question)}
                        handleDrag={() => handleDrag(question)}
                        question={question}
                      />
                    )
                  case 'true_false':
                    return (
                      <TrueFalseQuestion
                        handleDrop={() => handleDrop(question)}
                        handleDrag={() => handleDrag(question)}
                        question={question}
                      />
                    )
                  default:
                    return (
                      <RadioQuestion
                        handleDrop={() => handleDrop(question)}
                        handleDrag={() => handleDrag(question)}
                        question={question}
                      />
                    )
                }
              })}
            <Button
              startIcon={<AddRounded />}
              onClick={survey.addNewQuestion}
              text="Add Question"
              color="black"
              sx={{ marginTop: '32px' }}
            />
          </div>
        </div>
      </div>
    </>
  )
})

const ROOT: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
}
const CONTAINER: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  flex: 1,
  overflowY: 'auto',
  paddingBottom: 20,
}
const FORM: React.CSSProperties = {
  marginTop: 25,
  maxWidth: 660,
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
}
const SURVEY_TITLE_INPUT: React.CSSProperties = {
  fontSize: '28px',
  fontWeight: 600,
  lineHeight: '34px',
  fontStyle: 'normal',
}
